<script lang="ts">
  import type { Signer } from "@ethersproject/abstract-signer";
  import type { Contract } from "@ethersproject/contracts";
  import { getContext } from "svelte";
  import { nftContract, nftStakingContract } from "../../helpers/contracts";

  import { ethereum } from "../../store/ethereum";
  import Nft from "../nft/NFT.svelte";
  import NftImage from "../nft/NFTImage.svelte";
  import ErrorModal from "./ErrorModal.svelte";
  import TransactionModal from "./TransactionModal.svelte";

  interface NFT {
    id: number;
  }

  const { open, close } = getContext("simple-modal");

  let nfts: NFT[] = [];
  let loading = false;
  let approved = false;

  $: getCollectables($ethereum.signer);

  const getCollectables = async (signer: Signer) => {
    if (signer == undefined) return;
    const _nftContract = nftContract($ethereum.chainId).connect(signer);

    const inv = await _nftContract.walletInventory($ethereum.address);

    nfts = inv.map((id) => ({
      id: parseInt(id.toString()),
    }));

    console.log(
      "checking approval...",
      nftStakingContract($ethereum.chainId).address
    );

    try {
      approved = await _nftContract.isApprovedForAll(
        $ethereum.address,
        nftStakingContract($ethereum.chainId).address
      );
    } catch (err) {
      console.error("COULDNT check approval", _nftContract.functions);
    }
  };

  const awaitTx = async (res: any, msg: string) => {
    open(TransactionModal, {
      message: msg,
      details: `TxHash: ${res.hash}`,
      hash: res.hash,
    });
    await res.wait();
    close();
  };

  const handleTxError = (err: any) => {
    try {
      close();
    } catch {
      console.log("nothing to close");
    }

    let msg = err.toString();
    if (err.message && err.code)
      msg = `code: ${err.code}\nmessage: ${err.message}`;

    open(ErrorModal, { message: "Transaction Failed", details: msg });
    console.error(err);
  };

  //   const approve = async (id: number) => {
  //     if ($ethereum.signer == undefined) return;
  //     loading = true;

  //     try {
  //       const tx = await nftContract($ethereum.chainId)
  //         .connect($ethereum.signer)
  //         .approve(nftStakingContract($ethereum.chainId).address, id);
  //       await tx.wait();
  //       await getCollectables($ethereum.signer);
  //     } catch (err) {
  //       handleTxError(err);
  //     }
  //     loading = false;
  //   };

  const approveAll = async () => {
    if ($ethereum.signer == undefined) return;
    loading = true;
    try {
      const _nftContract = nftContract($ethereum.chainId).connect(
        $ethereum.signer
      );

      const tx = await _nftContract.setApprovalForAll(
        nftStakingContract($ethereum.chainId).address,
        true
      );
      await tx.wait();

      // update approval status
      approved = await _nftContract.isApprovedForAll(
        $ethereum.address,
        nftStakingContract($ethereum.chainId).address
      );
    } catch (err) {
      handleTxError(err);
    }
    loading = false;
  };

  const stake = async (id: number) => {
    if ($ethereum.signer == undefined) return;
    loading = true;

    const _nftStakingContract = nftStakingContract($ethereum.chainId);

    try {
      const gas = await _nftStakingContract
        .connect($ethereum.signer)
        .estimateGas.stake(id);

      const gasLimit = Math.max(gas.toNumber() + 5000, 240000);

      const tx = await _nftStakingContract
        .connect($ethereum.signer)
        .stake(id, { gasLimit: gasLimit.toString() });

      await awaitTx(tx, `Staking NFT #${id}`);
      setTimeout(() => {
          location.href = "/staking" 
      }, 100)
    } catch (err) {
      handleTxError(err);
    }

    loading = false;
  };

  const stakeAll = async () => {
    if ($ethereum.signer == undefined) return;
    loading = true;

    const _nftStakingContract = nftStakingContract($ethereum.chainId).connect(
      $ethereum.signer
    );

    try {
      const batch = nfts.map((n) => n.id);

      const gas = await _nftStakingContract.estimateGas.stakeBatch(batch);
      const gasLimit = gas.toNumber() + 3500 * batch.length;
      const tx = await _nftStakingContract.stakeBatch(batch, {
        gasLimit: gasLimit.toString(),
      });
      await awaitTx(tx, `Staking All NFTs`);
      setTimeout(() => {
          location.href = "/staking" 
      }, 100)
    } catch (err) {
      handleTxError(err);
    }

    loading = false;
    close();
  };
</script>

<h3>
  <i class="fas fa-exclamation-circle" />
  Stake you NFTs
</h3>

<p>
  You must first approve the staking contract to allow it to move your NFTs
  before they can be staked. <b>Only unstaked NFTs will show up here.</b>
</p>

{#if !approved}
  <button class="btn btn-primary btn-large" on:click={approveAll} disabled={loading}>
    {loading ? "Please wait..." : "Approve All"}
  </button>
{:else if nfts.length > 0}
  <button class="btn btn-primary btn-large" on:click={stakeAll} disabled={loading}>
    {loading ? "Please wait..." : "Stake All"}
  </button>
{/if}

{#if nfts.length > 0}
  <div class="flex flex-wrap">
    {#each nfts as nft}
      <div class="nft">
        <Nft id={nft.id}>
          <div class="nft-image">
            <NftImage style="width: 100%; border-radius: 10px;" />
          </div>
          <h3>#{nft.id}</h3>
          <button
            class="btn btn-primary btn-large"
            disabled={!approved || loading}
            on:click={() => stake(nft.id)}
          >
            {loading ? "Please wait..." : "Stake"}
          </button>
        </Nft>
      </div>
    {/each}
  </div>
{:else}
  <center>
    No unstaked Collectables :(
    <p>
      <b>Only unstaked NFTs will show up here.</b>
    </p>
  </center>
{/if}

<b>Only unstaked NFTs will show up here.</b>

<style>
  h3 {
    margin-top: 0px;
  }

  .flex {
    width: 100%;
    max-width: 100%;
  }

  i {
    font-size: 0.9rem;
    opacity: 0.5;
  }

  center {
    padding: 40px auto;
  }

  .nft {
    width: 200px;
    position: relative;
    background-color: var(--button-color);
    color: var(--button-text-color);
    overflow: none;
    padding: 10px;
    border-radius: var(--border-raduis);
    animation: popIn 0.3s ease backwards;
    transition: transform 0.5s ease, box-shadow 0.5s ease,
      background-color 0.5s ease;
    transform: scale(1);
    text-align: left;
    margin: 5px;
  }

  .nft-image {
    width: 100%;
  }

  .nft h3 {
    margin-bottom: 8px;
  }

  .nft .btn {
    width: 100%;
    margin-bottom: 5px;
  }
</style>
